import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { makeStyles } from "tss-react/mui";

import ObjectPanel from "../ObjectPanel";
import { showToast } from "../../../../../store/actions";
import { useCronReducer } from "../../../../../util/cron";
import WorkspaceService from "../../../../../service/workspace";
import { ButtonUI, ConfirmPopup } from "../../../../Common";
import { FormTile } from "../../../../Common/Tile";
import { setPrevState } from "../../../../../util/common";
import CommonLayout from "../../../../Common/Layout";
import DataSource from "../../../../../service/dataSource";
import { OBJECT_TYPE } from "../../../../../util/Data/DataModeling";
import { JOB_BY_TYPE, DETAUL_CRON_VALUE } from "../../utils/JobData";
import ComputeForm from "./ComputeForm";
import AddJobForm from "./AddJobForm";
import { navScheduledJobs } from "../../../../kmk/AppData";
import HelpContextIcon from "../../../../Common/HelpPanel/HelpContextIcon";
import NextJobForm from "../../utils/NextJobForm";
import Scheduler from "../../utils/Scheduler";
import DataPipelineService from "../../../../../service/dataPipelineService";
import SmallToolbar from "../../../../Common/SmallToolbar";
import CloneJob from "../../CloneJob";

const useStyles = makeStyles()((theme) => ({
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  BigTileContainer: {
    paddingTop: "0px !important",
    marginTop: "10px !important",
  },
  subContainer: {
    display: "flex",
    padding: 0,
    borderTop: 0,
    width: "100%",
  },
  PanelContainer: {
    padding: "22px 30px 30px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minHeight: "250px",
    marginLeft: "inherit",
    "& .MuiPaper-root": {
      height: "auto",
    },
  },
}));

export default function DedupScheduler() {
  const { Workspace_id, Job_id } = useParams();
  const workspace = useSelector((state) => state.workspace);
  const config = useSelector((state) => state.config);
  const clusterList = useSelector((state) => state.clusterList);
  const { Name: workspaceName, IsDataVaultEnabled: isDataVaultEnabled } = workspace.workspace;
  const defaultCluster = config.defaultCluster;

  const [initialLoading, setInitialLoading] = useState(true);
  const [jobState, setJobState] = useState({
    name: "",
    jobType: "",
    timeZone: "",
    nextRunTime: "",
    loading: false,
    scheduleEnabled: false,
    type: 6,
    closeSelect: false,
    DataPipeline_id: "",
    isEntryJob: false,
  });

  const [nextJobState, setNextJobState] = useState({
    nextJobToggle: false,
    nextDataPipelineId: "",
    nextJobName: "",
    nextJobId: "",
    activeSchedule: "",
    errorDataPipelineId: "",
    errorJobId: "",
    errorSchedule: "",
  });

  const [deleteJobState, setDeleteJobState] = useState({
    openConfirm: false,
    confirmTitle: "",
    confirmMessage: "",
    loadingForConfirm: false,
  });

  const [openClone, setOpenClone] = useState(false);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [cluster, setCluster] = useState(defaultCluster);
  const [dataPipelineList, setDataPipelineList] = useState([]);

  const nextJobIdRef = useRef(nextJobState.nextJobId);
  const errorJobIdRef = useRef(nextJobState.errorJobId);

  // For Schedule Panel
  const [values, dispatchValues] = useCronReducer(DETAUL_CRON_VALUE);

  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleCancel = () => {
    setPrevState(setDeleteJobState, { openConfirm: false });
  };

  const deleteJob = async () => {
    setPrevState(setDeleteJobState, { loadingForConfirm: true });

    const wsService = new WorkspaceService();
    const result = await wsService.deleteDeduplicationJob(Workspace_id, Job_id);

    if (result.success) {
      dispatch(
        showToast({
          message: result.message,
          type: "success",
        })
      );
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }
    setPrevState(setDeleteJobState, { openConfirm: false, loadingForConfirm: false });
  };

  const handleSave = async (e, clone = false) => {
    setPrevState(setJobState, { loading: true });

    const selectedEntities = entities.filter((source) => source.checked);
    const selectAll = selectedEntities.length === entities.length;

    const submitData = {
      name: jobState.name,
      type: jobState.type,
      DataPipeline_id: jobState.DataPipeline_id,
      schedule: values.cronValue || DETAUL_CRON_VALUE,
      scheduleEnabled: jobState.scheduleEnabled || false,
      cluster,
      enableNextJob: nextJobState.nextJobToggle,
      ...(nextJobState.nextJobToggle
        ? {
            nextPipeline: nextJobState.nextDataPipelineId,
            nextJob: nextJobState.nextJobId,
            errorPipeline: nextJobState.errorDataPipelineId,
            errorJob: nextJobState.errorJobId,
            disableNextJob: nextJobState.activeSchedule,
            disableErrorJob: nextJobState.errorSchedule,
          }
        : {
            nextPipeline: null,
            nextJob: null,
            errorPipeline: null,
            errorJob: null,
            disableNextJob: null,
            disableErrorJob: null,
          }),
      entity: { selectAll, entities: selectAll ? [] : selectedEntities.map(({ name }) => name) },
      isEntryJob: jobState.isEntryJob,
    };

    const wsService = new WorkspaceService();
    const response = Job_id
      ? await wsService.updateDataModelingJob(submitData, Workspace_id, Job_id, 6)
      : await wsService.createDataModelingJob(submitData, Workspace_id, 6);

    if (response.success) {
      if (clone) {
        setPrevState(setJobState, { loading: false });
        return true;
      }

      dispatch(showToast({ message: response.message, type: "success" }));
      navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
    }

    setPrevState(setJobState, { loading: false });
  };

  const handleCancelClick = useCallback(() => {
    navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);
  }, [Workspace_id, navigate]);

  const getJobSchedule = useCallback(async () => {
    const wsService = new WorkspaceService();
    const response = await wsService.getDeduplicationschedule(Workspace_id, Job_id);
    if (response === "Unauthorized" || !response.success)
      return navigate(`/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`);

    setPrevState(setJobState, {
      name: response.data.Name,
      jobType: response.data.Detail?.type,
      scheduleEnabled: response.data.Enabled,
      timeZone: response.data.ServerTimeZone,
      nextRunTime: response.data.nextRun,
      DataPipeline_id: response.data.DataPipeline_id,
    });

    setPrevState(setNextJobState, {
      nextDataPipelineId: response.data.NextPipeline_id || "",
      nextJobId: response.data.NextJob_id || nextJobIdRef.current,
      errorDataPipelineId: response.data.ErrorPipeline_id || "",
      errorJobId: response.data.ErrorJob_id || errorJobIdRef.current,
      nextJobToggle: response.data.enableNextJob || false,
    });

    dispatchValues({
      type: "set_values",
      value: response.data.Schedule,
    });

    return response.data;
  }, [Job_id, Workspace_id, dispatchValues, navigate]);

  const getEntities = useCallback(async () => {
    const datasource = new DataSource();

    const { success, data } = await datasource.getObjectNamesByType(
      Workspace_id,
      OBJECT_TYPE.Entity,
      JOB_BY_TYPE.dataDeduplication,
      Job_id
    );

    return success ? data : [];
  }, [Job_id, Workspace_id]);

  const getDataPipelineList = useCallback(async () => {
    const pipeline = new DataPipelineService();
    const response = await pipeline.getDataPipelineList(Workspace_id);
    if (response.success && response.data?.dataPipelineList?.length) return response.data.dataPipelineList;
    return [];
  }, [Workspace_id]);

  const resetNextJob = () =>
    setPrevState(setNextJobState, {
      activeSchedule: "",
      nextJobToggle: false,
      nextJobName: "",
      nextJobId: "",
      errorJobId: "",
      errorSchedule: "",
    });

  const confirmPipelineChange = useCallback((e) => {
    setJobState((prev) => ({
      ...prev,
      DataPipeline_id: e.target.value,
      ...(prev.DataPipeline_id ? { isEntryJob: false } : {}),
    }));
    resetNextJob();
  }, []);

  const fetchAllData = useCallback(async () => {
    if (clusterList === false) return;
    setInitialLoading(true);
    setEntityLoading(true);
    setPrevState(setJobState, { loading: true });

    let jobDetails = {};
    if (Job_id) jobDetails = await getJobSchedule();
    if (!jobDetails) return;

    let dataPipelines = await getDataPipelineList();
    if (!jobDetails.DataPipeline_id) setPrevState(setJobState, { DataPipeline_id: dataPipelines[0]?.DataPipeline_id });
    setDataPipelineList(dataPipelines);
    setInitialLoading(false);

    //Other details
    const entities = await getEntities();

    if (Job_id && clusterList?.length) {
      //Setting selected cluster
      setCluster(
        clusterList?.some((cluster) => cluster.value === jobDetails.Detail.Cluster)
          ? jobDetails.Detail.Cluster
          : defaultCluster
      );
    }

    //Setting selected refresh entities
    if (entities.length) {
      if (jobDetails?.DataSource?.selectAll === true) setEntities(entities.map((item) => ({ ...item, checked: true })));
      else {
        const selectedEntities = jobDetails?.DataSource?.entities || [];
        setEntities(entities.map((item) => ({ ...item, checked: !Job_id || selectedEntities.includes(item.name) })));
      }
    }

    let isEntryJob = false;
    if (Job_id) {
      const currentPipeline = dataPipelines.find(
        ({ DataPipeline_id }) => parseInt(DataPipeline_id) === parseInt(jobDetails.DataPipeline_id)
      );
      isEntryJob = currentPipeline.EntryJob_id === parseInt(Job_id);
    }

    setEntityLoading(false);
    setPrevState(setJobState, { loading: false, isEntryJob });
  }, [Job_id, getJobSchedule, getDataPipelineList, getEntities, clusterList, defaultCluster]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: `/my-workspaces/${Workspace_id}/${navScheduledJobs.link}`, displayName: "Data Pipelines", active: false },
    { link: "#", displayName: Job_id ? "Edit Data Deduplication Job" : "Add Data Deduplication Job", active: true },
  ];

  const disablesSave = useMemo(() => {
    return (
      jobState.name === "" ||
      jobState.loading ||
      cluster === "" ||
      (nextJobState.nextJobToggle === true && !(nextJobState.nextJobId || nextJobState.errorJobId)) ||
      (isDataVaultEnabled && entities.every((source) => !source.checked))
    );
  }, [
    jobState.name,
    jobState.loading,
    cluster,
    nextJobState.nextJobToggle,
    nextJobState.nextJobId,
    nextJobState.errorJobId,
    isDataVaultEnabled,
    entities,
  ]);

  const toggleSchedule = useCallback(
    () => setJobState((prev) => ({ ...prev, scheduleEnabled: !prev.scheduleEnabled })),
    []
  );

  return (
    <CommonLayout title="Job Scheduler" loading={jobState.loading} breadcrumbArr={breadcrumbArr}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          height: "100vh",
          overflowX: "hidden",
          overflowY: "auto",
          paddingBottom: "20px",
        }}
        id="main_box"
      >
        {initialLoading ? (
          <CircularProgress
            color="primary"
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
            }}
          />
        ) : (
          <>
            <Backdrop
              sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={jobState.loading || nextJobState.loading}
            >
              <CircularProgress color="primary" />
            </Backdrop>
            <Box maxWidth="lg" sx={{ mb: 4, mx: 0 }}>
              <Box className={classes.subContainer}>
                <Box className={classes.PanelContainer}>
                  <Grid container direction="row" sx={{ mb: 4 }}>
                    <Grid sx={{ mb: 1 }} size={12}>
                      {Job_id ? (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Edit Data Deduplication Job
                        </Typography>
                      ) : (
                        <Typography className={classes.HeaderTitle} gutterBottom component="div">
                          Add Data Deduplication Job
                        </Typography>
                      )}
                      <ButtonUI
                        onClick={handleSave}
                        variant="contained"
                        color="success"
                        disabled={disablesSave}
                        sx={{ mb: "5px" }}
                      >
                        Save
                      </ButtonUI>
                      <ButtonUI
                        onClick={handleCancelClick}
                        variant="contained"
                        color="secondary"
                        sx={{ mb: "5px", ml: "15px" }}
                      >
                        Cancel
                      </ButtonUI>

                      {Job_id ? (
                        <>
                          <ButtonUI
                            onClick={() => setOpenClone(true)}
                            variant="contained"
                            color="info"
                            sx={{ mb: "5px", mx: "15px" }}
                            disabled={disablesSave}
                          >
                            Clone
                          </ButtonUI>

                          <ButtonUI
                            variant="contained"
                            color="error"
                            onClick={() => {
                              setPrevState(setDeleteJobState, {
                                openConfirm: true,
                                confirmTitle: "Delete Data Deduplication Job",
                                confirmMessage: `Are you sure you want to delete "${jobState.name}" Job?"`,
                              });
                            }}
                            disabled={!Job_id}
                            sx={{ mb: "5px" }}
                          >
                            Delete
                          </ButtonUI>
                        </>
                      ) : (
                        <></>
                      )}

                      <Box sx={{ display: "inline-flex", verticalAlign: "sub" }}>
                        <HelpContextIcon screen="Data Deduplication Job" />
                      </Box>
                    </Grid>
                    <Grid className={classes.BigTileContainer} size={12}>
                      <FormTile>
                        <AddJobForm
                          jobState={jobState}
                          setJobState={setJobState}
                          dataPipelineList={dataPipelineList}
                          confirmPipelineChange={confirmPipelineChange}
                          Job_id={Job_id}
                        />
                      </FormTile>
                    </Grid>
                    <Grid className={classes.BigTileContainer} size={12}>
                      <SmallToolbar>Schedule</SmallToolbar>
                      <FormTile
                        style={{
                          overflowX: "auto",
                        }}
                      >
                        <Scheduler
                          scheduleEnabled={jobState.scheduleEnabled}
                          cronValue={values.cronValue}
                          dispatchValues={dispatchValues}
                          toggleValue={toggleSchedule}
                          timeZone={jobState.timeZone}
                          nextRunTime={jobState.nextRunTime}
                        />
                      </FormTile>
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <ObjectPanel
                        objectType={OBJECT_TYPE.Entity}
                        objects={entities}
                        setObjects={setEntities}
                        loading={entityLoading}
                        setLoading={setEntityLoading}
                        getObjects={getEntities}
                      />
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Compute Clusters</SmallToolbar>
                      <FormTile>
                        <ComputeForm clusterList={clusterList} cluster={cluster} setComputeState={setCluster} />
                      </FormTile>
                    </Grid>

                    <Grid sx={{ paddingTop: "24px !important" }} size={12}>
                      <SmallToolbar>Next Job</SmallToolbar>
                      <FormTile>
                        <NextJobForm
                          dataPipelineList={dataPipelineList}
                          nextJobToggle={nextJobState.nextJobToggle}
                          nextDataPipelineId={nextJobState.nextDataPipelineId}
                          nextJobId={nextJobState.nextJobId}
                          errorDataPipelineId={nextJobState.errorDataPipelineId}
                          errorJobId={nextJobState.errorJobId}
                          setNextJobState={setNextJobState}
                        />
                      </FormTile>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Box>

      {openClone && (
        <CloneJob
          setOpenClone={setOpenClone}
          pipelineId={jobState.DataPipeline_id}
          handleSave={handleSave}
          JobType={JOB_BY_TYPE.dataDeduplication}
        />
      )}

      {deleteJobState.openConfirm && (
        <ConfirmPopup
          configData={{
            popupTitle: deleteJobState.confirmTitle,
            popupMessage: deleteJobState.confirmMessage,
            handleCancel: handleCancel,
            handleClick: deleteJob,
            loadingForConfirm: deleteJobState.loadingForConfirm,
          }}
        />
      )}
    </CommonLayout>
  );
}
